import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  ArrowRight,
  Search,
  Play,
  Bot,
  FileText,
  Users,
  Clock,
  UserRound,
  MessagesSquare,
  Lock,
  Video,
  Quote,
  ChevronDown,
  Filter,
  FileX,
  Ban,
  Briefcase,
} from "lucide-react";
import PageSeo from "../components/PageSeo";
import PageTopbar from "../components/PageTopbar";
import YoutubeVideoLightbox from "../components/YoutubeVideoLightbox";
import YoutubeThumbnail from "../components/YoutubeThumbnail";
import { APPLYING_FOR_JOBS_VIDEO_ID } from "../lib/youtubeVideos";
import { buildBreadcrumbNavItems } from "../lib/pageSeo";
import Nd1SiteNav from "./components/Nd1SiteNav";
import Nd1SiteFooter from "./components/Nd1SiteFooter";
import { Nd1GreenTickIcon, Nd1OrangeTickIcon } from "./components/Nd1TickIcons";
import "./nd1.css";

const HERO_PERKS = [
  { icon: ShieldCheck, lines: ["Verified", "employers"] },
  { icon: UserRound, lines: ["Stand out", "as real you"] },
  { icon: MessagesSquare, lines: ["Better", "conversations"] },
  { icon: Lock, lines: ["Safe &", "transparent"] },
];

const PROBLEMS = [
  {
    icon: Bot,
    title: "AI can apply to hundreds of jobs",
    copy: "Automated applications flood inboxes — making it harder for real people to get noticed.",
  },
  {
    icon: FileText,
    title: "CVs all sound the same",
    copy: "Keyword-stuffed résumés blur together, so genuine skills and motivation get lost.",
  },
  {
    icon: Users,
    title: "Genuine people get lost",
    copy: "The candidates who'd actually be great for the role disappear in a pile of polished noise.",
  },
  {
    icon: Clock,
    title: "Real conversations happen too late",
    copy: "By the time a human reads your application, the best opportunities may already be gone.",
  },
];

const TESTIMONIALS = [
  {
    quote: "Finally, a chance to explain myself.",
    copy: "I stopped rewriting my CV for the tenth time and actually showed who I am. It felt like someone was listening.",
    name: "James R.",
    role: "Marketing Manager",
    initials: "JR",
  },
  {
    quote: "Less CV rewriting, more real conversations.",
    copy: "Instead of guessing what keywords to add, I could introduce myself properly and talk about what I actually want.",
    name: "Sarah K.",
    role: "Customer Support",
    initials: "SK",
  },
  {
    quote: "I felt like a person, not a keyword.",
    copy: "For the first time in ages, applying didn't feel like shouting into a void. I could be myself.",
    name: "Alex M.",
    role: "Software Engineer",
    initials: "AM",
  },
];

const FIND_JOBS_POINTS = [
  "Reviewed employers and genuine job opportunities",
  "Less spam and misleading listings",
  "Roles worth your time and energy",
  "A clearer path to the right fit",
];

const STAND_OUT_POINTS = [
  "Video introductions that show who you really are",
  "A stronger signal than another keyword-stuffed CV",
  "Personality, motivation, and communication style",
  "Help hiring managers see you, not just your document",
];

const TRADITIONAL_FLOW = [
  { icon: Briefcase, label: "1000 Applications" },
  { icon: Filter, label: "ATS Filters" },
  { icon: FileText, label: "Keyword Matching" },
  { icon: FileX, label: "Generic Rejections" },
  { icon: Ban, label: "No Human Connection" },
];

const JOBUWIN_FLOW = [
  { icon: Briefcase, label: "Genuine Jobs" },
  { icon: ShieldCheck, label: "Verified Employers" },
  { icon: Video, label: "Video Introductions" },
  { icon: UserRound, label: "Genuine Applicant Signals" },
  { icon: MessagesSquare, label: "Better Conversations" },
];

const STEPS = [
  {
    icon: Search,
    title: "Find a role",
    copy: "Browse jobs from reviewed employers.",
  },
  {
    icon: Video,
    title: "Introduce yourself",
    copy: "Show who you are, not just what's on your CV.",
  },
  {
    icon: MessagesSquare,
    title: "Connect",
    copy: "Start better conversations with hiring managers.",
  },
];

export default function Nd1JobSeekersPage() {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="nd1-root nd1-root--seekers">
      <PageSeo
        title="Job seekers — stand out as the real you"
        description="Stop competing with AI-generated applications. JOBUWIN helps genuine job seekers stand out through authentic communication — not keyword tricks."
        path="/job-seekers"
        canonicalPath="/job-seekers"
        schemaType="website"
      />

      <a href="#nd1-main" className="nd1-skip-link">
        Skip to content
      </a>

      <Nd1SiteNav />

      <main id="nd1-main">
        {/* Hero */}
        <section className="nd1-shell nd1-js-hero">
          <PageTopbar
            breadcrumbsOnly
            breadcrumbItems={buildBreadcrumbNavItems([
              { name: "Home", path: "/" },
              { name: "For job seekers", path: "/job-seekers", current: true },
            ])}
          />
          <div className="nd1-js-hero__grid">
            <div className="nd1-js-hero__content">
              <span className="nd1-eyebrow-chip">
                <ShieldCheck className="nd1-icon" aria-hidden="true" />
                Verified hiring for real people
              </span>

              <h1 className="nd1-js-hero__title">
                Stop competing with <span className="nd1-ink-green">AI generated</span> applications.
              </h1>

              <p className="nd1-js-hero__copy">
                AI can apply to hundreds of jobs in minutes — flooding hiring teams with noise and burying
                genuine candidates. JOBUWIN helps you stand out through authentic communication, not keyword
                tricks.
              </p>

              <Link to="/signup?type=applicant" className="nd1-btn nd1-btn--primary nd1-js-hero__cta">
                <Search className="nd1-icon" aria-hidden="true" />
                Find genuine jobs
              </Link>
            </div>

            <div className="nd1-js-hero__media">
              <button
                type="button"
                className="nd1-js-hero__video"
                onClick={() => setVideoOpen(true)}
                aria-label="Play video: Why apply through JOBUWIN"
              >
                <YoutubeThumbnail
                  videoId={APPLYING_FOR_JOBS_VIDEO_ID}
                  alt="Why apply through JOBUWIN"
                  loading="eager"
                  fetchPriority="high"
                />
                <span className="nd1-js-hero__video-play" aria-hidden="true">
                  <Play className="nd1-icon" />
                </span>
              </button>

              <div className="nd1-js-hero__perks">
                <p className="nd1-js-hero__perks-title">Why apply through JOBUWIN?</p>
                <ul className="nd1-js-hero__perk-list">
                  {HERO_PERKS.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li key={item.lines.join(" ")} className="nd1-js-hero__perk">
                        <span className="nd1-js-hero__perk-icon">
                          <Icon className="nd1-icon" aria-hidden="true" />
                        </span>
                        <span className="nd1-js-hero__perk-label">
                          {item.lines.map((line) => (
                            <span key={line} className="nd1-js-hero__perk-line">
                              {line}
                            </span>
                          ))}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* The problem today */}
        <section className="nd1-shell nd1-section">
          <div className="nd1-hm-section-head">
            <h2 className="nd1-hm-section-head__title">The problem today</h2>
          </div>

          <div className="nd1-js-problem-grid">
            {PROBLEMS.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title} className="nd1-card nd1-js-problem-card">
                  <span className="nd1-js-problem-card__icon">
                    <Icon className="nd1-icon" aria-hidden="true" />
                  </span>
                  <h3 className="nd1-js-problem-card__title">{item.title}</h3>
                  <p className="nd1-js-problem-card__copy">{item.copy}</p>
                </article>
              );
            })}
          </div>
        </section>

        {/* What applicants love */}
        <section className="nd1-shell nd1-section">
          <div className="nd1-hm-section-head">
            <h2 className="nd1-hm-section-head__title">What applicants love</h2>
          </div>

          <div className="nd1-js-testimonial-grid">
            {TESTIMONIALS.map((item) => (
              <article key={item.name} className="nd1-card nd1-js-testimonial">
                <Quote className="nd1-icon nd1-js-testimonial__quote" aria-hidden="true" />
                <h3 className="nd1-js-testimonial__headline">&ldquo;{item.quote}&rdquo;</h3>
                <p className="nd1-js-testimonial__copy">{item.copy}</p>
                <footer className="nd1-js-testimonial__author">
                  <span className="nd1-js-testimonial__avatar" aria-hidden="true">
                    {item.initials}
                  </span>
                  <span>
                    <strong>{item.name}</strong>
                    <span className="nd1-js-testimonial__role">{item.role}</span>
                  </span>
                </footer>
              </article>
            ))}
          </div>
        </section>

        {/* How JOBUWIN helps */}
        <section className="nd1-shell nd1-section">
          <div className="nd1-hm-section-head nd1-hm-section-head--wide">
            <h2 className="nd1-hm-section-head__title">How JOBUWIN helps</h2>
            <p className="nd1-hm-section-head__copy">
              Whether you&apos;re searching for your next role or trying to break through the noise, JOBUWIN
              gives you a more human way to be seen.
            </p>
          </div>

          <div className="nd1-js-help-grid">
            <article className="nd1-js-help-card nd1-js-help-card--green">
              <span className="nd1-js-help-card__icon">
                <Search className="nd1-icon" aria-hidden="true" />
              </span>
              <h3 className="nd1-js-help-card__title">Find genuine jobs</h3>
              <p className="nd1-js-help-card__copy">
                Browse roles from reviewed employers — with less spam, fewer misleading listings, and a clearer
                path to opportunities worth your time.
              </p>
              <ul className="nd1-check-list">
                {FIND_JOBS_POINTS.map((item) => (
                  <li key={item} className="nd1-check-list__item">
                    <Nd1GreenTickIcon className="nd1-icon" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/signup?type=applicant" className="nd1-js-help-card__link">
                Find genuine jobs
                <ArrowRight className="nd1-icon" aria-hidden="true" />
              </Link>
            </article>

            <article className="nd1-js-help-card nd1-js-help-card--orange">
              <span className="nd1-js-help-card__icon nd1-js-help-card__icon--orange">
                <Video className="nd1-icon" aria-hidden="true" />
              </span>
              <h3 className="nd1-js-help-card__title">Stand out in a more human way</h3>
              <p className="nd1-js-help-card__copy">
                Introduce yourself with video and show hiring managers who you really are — not just another
                keyword-stuffed CV in the pile.
              </p>
              <ul className="nd1-check-list nd1-check-list--orange">
                {STAND_OUT_POINTS.map((item) => (
                  <li key={item} className="nd1-check-list__item">
                    <Nd1OrangeTickIcon className="nd1-icon" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/signup?type=applicant" className="nd1-js-help-card__link nd1-js-help-card__link--orange">
                See how applications work
                <ArrowRight className="nd1-icon" aria-hidden="true" />
              </Link>
            </article>
          </div>
        </section>

        {/* Why JOBUWIN is different */}
        <section className="nd1-shell nd1-section">
          <div className="nd1-hm-section-head nd1-hm-section-head--wide">
            <h2 className="nd1-hm-section-head__title">Why JOBUWIN is different</h2>
            <p className="nd1-hm-section-head__copy">
              Most hiring platforms start with documents. JOBUWIN starts with people.
            </p>
          </div>

          <div className="nd1-js-diff">
            <div className="nd1-js-diff__col nd1-js-diff__col--traditional">
              <h3 className="nd1-js-diff__heading">Traditional applications</h3>
              <ol className="nd1-js-diff__flow">
                {TRADITIONAL_FLOW.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.label} className="nd1-js-diff__step">
                      <span className="nd1-js-diff__step-icon nd1-js-diff__step-icon--muted">
                        <Icon className="nd1-icon" aria-hidden="true" />
                      </span>
                      <span>{item.label}</span>
                      {index < TRADITIONAL_FLOW.length - 1 ? (
                        <ChevronDown className="nd1-icon nd1-js-diff__arrow-down" aria-hidden="true" />
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            </div>

            <div className="nd1-js-diff__bridge" aria-hidden="true">
              <ArrowRight className="nd1-icon" />
            </div>

            <div className="nd1-js-diff__col nd1-js-diff__col--jobuwin">
              <h3 className="nd1-js-diff__heading nd1-js-diff__heading--jobuwin">JOBUWIN</h3>
              <ol className="nd1-js-diff__flow">
                {JOBUWIN_FLOW.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.label} className="nd1-js-diff__step">
                      <span className="nd1-js-diff__step-icon">
                        <Icon className="nd1-icon" aria-hidden="true" />
                      </span>
                      <span>{item.label}</span>
                      {index < JOBUWIN_FLOW.length - 1 ? (
                        <ChevronDown className="nd1-icon nd1-js-diff__arrow-down" aria-hidden="true" />
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="nd1-shell nd1-section nd1-js-how">
          <div className="nd1-hm-section-head">
            <h2 className="nd1-hm-section-head__title">How it works</h2>
          </div>

          <div className="nd1-js-steps">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={step.title} className="nd1-js-step">
                  <div className="nd1-js-step__lead">
                    <span className="nd1-js-step__num">{index + 1}</span>
                    <span className="nd1-js-step__icon">
                      <Icon className="nd1-icon" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="nd1-js-step__text">
                    <h3 className="nd1-js-step__title">{step.title}</h3>
                    <p className="nd1-js-step__copy">{step.copy}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Footer CTA */}
        <section className="nd1-shell nd1-section nd1-section--tight">
          <div className="nd1-js-cta-band">
            <span className="nd1-js-cta-band__icon">
              <UserRound className="nd1-icon" aria-hidden="true" />
            </span>
            <p className="nd1-js-cta-band__copy">
              Your next opportunity <span className="nd1-ink-green">deserves more than a CV.</span>
            </p>
            <Link to="/signup?type=applicant" className="nd1-btn nd1-btn--primary">
              <Search className="nd1-icon" aria-hidden="true" />
              Find genuine jobs
            </Link>
          </div>
        </section>
      </main>

      <Nd1SiteFooter />

      <YoutubeVideoLightbox
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        videoId={APPLYING_FOR_JOBS_VIDEO_ID}
        title="Why apply through JOBUWIN"
      />
    </div>
  );
}
